const en = {
  language: {
    label: "Language",
    english: "English",
    arabic: "Arabic",
  },
  shell: {
    educationOs: "Education OS",
    welcome: "Welcome, {name}",
    logout: "Log out",
    navigation: "Navigation",
  },
  login: {
    signIn: "Sign in",
    access: "Access your dashboard as staff or parent",
    email: "Email",
    password: "Password",
    submit: "Sign in",
    submitting: "Signing in…",
    demos: "Try a demo account",
    demoPassword: "Password for all demo accounts:",
    welcome: "Welcome to EduOS",
    platformBadge: "School operations, connected",
    emailPlaceholder: "you@school.ae",
    heroLead: "One platform for the",
    heroAccent: "complete student journey.",
    heroDescription: "From nursery admission to alumni — admissions, fees, attendance, and parent communication in one configurable ecosystem.",
    admissionFeature: "Admissions to Enrollment",
    admissionFeatureDetail: "Full pipeline",
    recordsFeature: "360° Student Records",
    recordsFeatureDetail: "SIS + Guardians",
    feesFeature: "Fees & Ledger",
    feesFeatureDetail: "Immutable invoices",
    circularsFeature: "Parent Circulars",
    circularsFeatureDetail: "Email + Read receipts",
    trustFooter: "Built with tenant isolation · Integer-paise accounting · Audit trails on every mutation",
  },
  account: {
    title: "Your EduOS account",
    signedInAs: "You are signed in as {name}.",
    recognizedRoles: "Recognized roles",
    permissionNotice: "Module permissions are assigned separately. Contact the nursery owner if you need access to a workflow.",
  },
  calendar: {
    previousMonth: "Previous month",
    nextMonth: "Next month",
    previousYear: "Previous year",
    nextYear: "Next year",
    previousYears: "Earlier years",
    nextYears: "Later years",
    chooseMonth: "Choose month",
    chooseYear: "Choose year",
  },
  common: {
    cancel: "Cancel",
    saving: "Saving…",
    save: "Save",
    edit: "Edit",
    delete: "Delete",
    view: "View",
    download: "Download",
    close: "Close",
  },
  passwordChange: { title: "Set your password", subtitle: "Replace your temporary password before continuing.", current: "Current temporary password", new: "New password", confirm: "Confirm new password", guidance: "At least 12 characters with uppercase, lowercase, a number, and a symbol.", submit: "Update password" },
  admissions: {
    selectSectionTitle: "Select section",
    selectSectionDescription: "Choose a section with available capacity before enrolling this child.",
    section: "Section",
    chooseSection: "Choose a section",
    enroll: "Enroll",
    enrollSetsStatus: "Enrolling creates the student record and sets the status to Enrolled.",
  },
  admissionWizard: {
    title: "New admission application",
    newDraft: "Start a secure family application",
    savedDraft: "Saved draft — changes are shared with the admissions team",
    notDraft: "This application can no longer be edited as a draft.",
    back: "Back to admissions",
    progress: "Application progress",
    saveExit: "Save & exit",
    saveContinue: "Save & continue",
    previous: "Previous",
    submit: "Submit application",
    draftSaved: "Draft saved",
    draftCount: "{count} saved drafts",
    noDrafts: "No saved drafts.",
    untitled: "Untitled",
    familyPending: "Family details pending",
    loading: "Loading application settings…",
    loadError: "Application settings could not be loaded.",
    retry: "Try again",
    submitted: "Application {number} submitted",
    steps: { child: "Child", parents: "Parents", documents: "Documents", review: "Review" },
    people: { child: "Child", father: "Father", mother: "Mother" },
    child: { title: "Child and placement", description: "Add the child's identity, address, and requested school placement." },
    parents: {
      title: "Parents and contacts",
      description: "Record separate father and mother details and choose the family contacts.",
      primary: "Primary contact",
      makePrimary: "Make primary contact",
      emergency: "Emergency contact",
    },
    fields: {
      firstName: "First name", lastName: "Last name", fullName: "Full name", dob: "Date of birth",
      birthPlace: "Place of birth", gender: "Gender", nationality: "Nationality", grade: "Grade",
      year: "Academic year", street: "Street address", apartment: "Apartment / unit", email: "Email",
      phone: "Phone", education: "Education level", occupation: "Occupation", workplace: "Place of work",
      workDetails: "Work details", select: "Select",
    },
    documents: {
      title: "Family documents",
      description: "Upload passport copies for all three people. Emirates ID and Visa are optional and may be provided if available or applicable.",
      required: "Required before submission", optional: "Optional", optionalIfApplicable: "Optional — provide if available or applicable",
      number: "Document number", expiry: "Expiry date", upload: "Upload files", uploading: "Uploading…",
    },
    review: {
      title: "Review and submit", description: "Check the family details and document completion before entering the admissions pipeline.",
      documents: "Required documents {count}", missing: "Complete the following items", privacy: "Identity files are stored as sensitive documents and are available only through authorized profile or application access.",
    },
  },
  guardianActivation: {
    title: "Activate guardian account",
    description: "Set a temporary password for {name}. They must replace it on first login.",
    password: "Temporary password",
    activate: "Activate account",
    active: "Active",
    inactive: "Inactive",
    documents: "{count} identity documents",
    success: "Guardian account activated",
  },
  forbidden: {
    title: "Access restricted",
    message: "Your role does not have permission to use this workflow.",
    back: "Go back",
  },
  roles: {
    platform_admin: "Platform administrator",
    tenant_owner: "Owner",
    administrator: "Administrator",
    principal: "Principal",
    accountant: "Accountant",
    teacher: "Teacher",
    guardian: "Guardian",
  },
  nav: {
    inventory: "Inventory & Supplies",
    dashboard: "Dashboard",
    admissions: "Admissions",
    students: "Students",
    attendance: "Attendance",
    timetable: "Timetable",
    exams: "Exams",
    library: "Library",
    services: "Services",
    announcements: "Announcements",
    erp: "ERP",
    campus: "Campus",
    nursery: "Nursery / EYFS",
    fees: "Fees",
    circulars: "Circulars",
    academicSetup: "Academic Setup",
    staff: "Staff",
    dcuments: "Documents",
    institution: "Institution",
    platformSettings: "Platform Settings",
    branding: "Branding",
    platformConfiguration: "Platform Config",
    platformLookups: "Platform Lookups",
    reports: "Reports",
    kiosk: "Kiosk",
    home: "Home",
    homework: "Homework",
    marks: "Marks",
    study: "Study",
    gallery: "Activity Photos",
    account: "Account",
  },
  inventory: {
    title: "Inventory & Supplies",
    subtitle: "Keep classroom supplies visible, priced, and ready for the next school day.",
    managementLabel: "Stock management",
    summary: "Inventory summary",
    totalItems: "Catalogue items",
    activeItems: "Active stock records",
    availableUnits: "Available units",
    currentBalance: "Current balance",
    lowStock: "Low stock",
    needsAttention: "Needs attention",
    catalogueValue: "Catalogue value",
    availableValue: "Available stock value",
    catalogueTitle: "Stock Catalogue & Levels",
    catalogueDescription: "Search, review, and maintain the supplies currently available to your nursery.",
    lowStockCount: "{count} items need attention",
    searchPlaceholder: "Search by item, SKU, or category…",
    allCategories: "All categories",
    allStatuses: "All statuses",
    healthyStock: "Healthy stock",
    item: "Item",
    category: "Category",
    available: "Available",
    unitPrice: "Unit price (AED)",
    target: "Target",
    targetStock: "Target stock",
    alertPercent: "Low-stock alert (%)",
    defaultAlert: "Use tenant default",
    status: "Status",
    actions: "Actions",
    low: "Low",
    healthy: "Healthy",
    archive: "Archive",
    showing: "Showing {shown} of {total} items",
    priceNote: "Prices are shown in AED",
    loading: "Loading stock catalogue…",
    empty: "No stock items yet",
    emptyDescription: "Add your first classroom supply to start building the catalogue.",
    noMatches: "No matching stock items",
    noMatchesDescription: "Try a different search term or clear the filters.",
    clearFilters: "Clear filters",
    managementOnly: "The stock catalogue is available to management users only.",
    addStock: "Add stock",
    addTitle: "Add stock item",
    addDescription: "Create a catalogue item with its unit, target level, and AED price.",
    editTitle: "Edit stock item",
    editDescription: "Update the catalogue details and low-stock settings. Historical movements are preserved.",
    itemName: "Item name",
    itemNamePlaceholder: "e.g. Whiteboard marker — blue",
    sku: "SKU",
    unit: "Unit",
    selectCategory: "Select category",
    selectUnit: "Select unit",
    priceHelp: "Price is collected in AED and stored in minor currency units.",
    saveChanges: "Save changes",
    addSuccess: "Stock item added",
    editSuccess: "Stock item updated",
    archiveSuccess: "Stock item archived",
    archiveTitle: "Archive this stock item?",
    archiveDescription: "{item} will be removed from active catalogue views. Existing stock history will remain available.",
  },
  dashboard: {
    title: "Dashboard",
    attendanceToday: "Attendance today",
    attendanceSummary: "{present} present · {absent} absent · {unmarked} unmarked",
    collectionsToday: "Collections today",
    mtd: "MTD",
    outstanding: "Outstanding",
    pendingInvoices: "Pending invoices",
    totalStudents: "Total students",
    enrolled: "Enrolled",
    admissionsFunnel: "Admissions funnel",
    newToday: "{count} new today",
    noApplications: "No applications yet.",
    quickActions: "Quick actions",
    reviewApplications: "Review applications",
    composeCircular: "Compose circular",
    generateInvoices: "Generate invoices",
    viewStudents: "View students",
    loadError: "Unable to load the dashboard.",
    alerts: {
      attendanceBelow: "Attendance below 80% today ({percentage}%)",
      outstandingThreshold: "Outstanding dues exceed {amount}",
      unmarkedSections: "{count} of {total} sections have not marked attendance today",
    },
    funnel: {
      enquiry: "Enquiry",
      applied: "Applied",
      documents_pending: "Documents pending",
      shortlisted: "Shortlisted",
      interview_scheduled: "Interview scheduled",
      offered: "Offered",
      fee_paid: "Fee paid",
      enrolled: "Enrolled",
    },
  },
  guardian: {
    today: "Today",
    yourChild: "Your child",
    yourWards: "Your {count} wards",
    present: "Present",
    absent: "Absent",
    late: "Late",
    half_day: "Half day",
    excused: "Excused",
    notMarked: "Not marked",
    attendance30d: "30-day attendance",
    dues: "Dues",
    feesDue: "Fees due",
    view: "View",
    viewAll: "View all",
    allCaughtUp: "All caught up. Nothing due right now.",
    acrossInvoices: "across {count} invoice{plural}",
    unreadCirculars: "Unread circulars",
    noNewCirculars: "No new circulars.",
    noWards: "No wards linked to your account yet.",
    feesTitle: "Fees",
    feesSubtitle: "All invoices for your ward(s)",
    totalOutstanding: "Total outstanding",
    unpaidOfTotal: "{unpaid} unpaid · {total} total",
    dueOn: "Due {date}",
    total: "Total",
    paid: "Paid",
    balance: "Balance",
    payOnline: "Pay online",
    startingPayment: "Starting payment…",
    invoicePdf: "Invoice PDF",
    paymentHistory: "Payment history",
    loading: "Loading…",
    noPaymentsYet: "No payments recorded yet.",
    date: "Date",
    receipt: "Receipt",
    method: "Method",
    amount: "Amount",
    status: "Status",
    action: "Action",
    noInvoicesYet: "No invoices yet.",
    studyTitle: "Study",
    studySubtitle: "Homework, exams and report cards",
    homework: "Homework",
    exams: "Exams",
    reportCards: "Report Cards",
    submitted: "Submitted",
    noHomeworkYet: "No homework yet.",
    graded: "Graded",
    yourSubmission: "Your submission",
    submissionPlaceholder: "Type or paste your work",
    submit: "Submit",
    submitting: "Submitting...",
    upcomingExams: "Upcoming / recent exams",
    marks: "Marks",
    noMarksYet: "No marks published yet.",
    reportCardTitle: "Report Card · {exam}",
    overallGrade: "Overall grade",
    percentage: "Percentage",
    subject: "Subject",
    grade: "Grade",
    teacherRemark: "Class teacher's remark",
    noReportCardsYet: "No report cards yet.",
    servicesTitle: "Services",
    servicesSubtitle: "Bus, events and meals",
    bus: "Bus",
    events: "Events",
    meals: "Meals",
    noBusAssigned: "No bus assigned yet.",
    assignedRoute: "Assigned route",
    driver: "Driver",
    conductor: "Conductor",
    routeStops: "Route stops",
    pickup: "Pickup",
    drop: "Drop",
    pickupDropAt: "Pickup {pickup} · Drop {drop}",
    yes: "Yes",
    no: "No",
    rsvpSaved: "RSVP saved",
    noUpcomingEvents: "No upcoming events.",
    weekOf: "Week of {date}",
    breakfast: "Breakfast:",
    lunch: "Lunch:",
    snacks: "Snacks:",
    noMenuPublished: "No menu published.",
    day0: "Mon",
    day1: "Tue",
    day2: "Wed",
    day3: "Thu",
    day4: "Fri",
    day5: "Sat",
    day6: "Sun",
    activityPhotosTitle: "Activity Photos & Videos",
    activityPhotosSubtitle: "Relive your child's school day — photos and videos from the classroom and events",
    activityPhotosHero: "Your child's moments, captured and shared",
    activityPhotosEmpty: "No albums yet. When teachers share new photos, they will appear here.",
    activityPhotosRecent: "Recent activity",
    activityPhotosViewAll: "Open gallery",
    albumsCount: "{count} albums",
    mediaItemsCount: "{count} media",
    gallerySearchPlaceholder: "Search albums…",
    galleryConsentTitle: "Photo sharing settings",
    galleryConsentHint: "Control whether photos of your child can be shared with you. You can change this anytime.",
    galleryConsentUpdated: "Photo sharing preference updated",
    galleryConsentOn: "Photos are visible to you",
    galleryConsentOff: "Photos are paused for this child",
    galleryNoSearchResults: "No albums match your search.",
    galleryNoMediaInAlbum: "No photos or videos in this album yet.",
    galleryLightbox: "Photo viewer",
    mediaCount: "{count} photos & videos",
    circularsTitle: "Circulars",
    circularsSubtitle: "School announcements & notices",
    noCircularsYet: "No circulars yet.",
    paymentNoPending: "No pending payment to complete.",
    backToFees: "Back to fees",
    securePaymentSim: "Secure payment (simulation)",
    hostedCheckout: "Network International hosted checkout",
    card: "Card",
    cancel: "Cancel",
    fail: "Fail",
    payNow: "Pay now",
    paymentSuccessful: "Payment successful",
    paymentCancelled: "Payment cancelled",
    paymentFailed: "Payment failed",
    statuses: {
      active: "Active",
      enrolled: "Enrolled",
      inactive: "Inactive",
      withdrawn: "Withdrawn",
      transferred: "Transferred",
      issued: "Issued",
      partially_paid: "Partially paid",
      overdue: "Overdue",
      paid: "Paid",
      voided: "Cancelled",
      refunded: "Refunded",
      success: "Success",
      failed: "Failed",
      cancelled: "Cancelled",
      initiated: "Initiated",
    },
    methods: {
      online: "Online",
      cash: "Cash",
      bank_transfer: "Bank transfer",
      pos: "POS",
      cheque: "Cheque",
      neft: "NEFT",
    },
  },
  teacher: {
    greeting: "Namaste, {name}",
    yourSections: "Your sections",
    gradeSection: "{grade} · Section {name}",
    studentsCount: "{count} students",
    attendancePending: "Attendance pending",
    noSectionsAssigned: "No sections assigned.",
    latestCirculars: "Latest circulars",
    markAttendance: "Mark Attendance",
    tapOnceSubtitle: "Tap once to mark, or use bulk actions",
    section: "Section",
    chooseSection: "Choose section",
    date: "Date",
    lockedWarning: "Attendance for this date is locked",
    allLabel: "All {label} ({count})",
    present: "Present",
    absent: "Absent",
    late: "Late",
    markAria: "Mark {label}",
    emptySelectSection: "Select a section to mark attendance",
    saveMarked: "Save · {marked}/{total} marked",
    saving: "Saving...",
    attendanceMarkedToast: "Attendance marked for {count} students",
    homeworkTitle: "Homework",
    homeworkSubtitle: "Post assignments and review submissions",
    new: "New",
    postHomework: "Post homework",
    subject: "Subject",
    title: "Title",
    instructions: "Instructions",
    dueDate: "Due date",
    maxMarks: "Max marks",
    posting: "Posting...",
    dueOn: "Due {date}",
    noHomeworkYet: "No homework yet.",
    submissionsCount: "Submissions ({count})",
    noSubmissionsYet: "No submissions yet.",
    submittedOn: "Submitted {date}",
    graded: "Graded",
    marksPlaceholder: "Marks (max {max})",
    feedback: "Feedback",
    saveGrade: "Save grade",
    gradedToast: "Graded",
    homeworkPostedToast: "Homework posted",
    marksEntry: "Marks entry",
    marksSubtitle: "Enter exam marks per subject",
    exam: "Exam",
    chooseExam: "Choose exam",
    marksLockedWarning: "Marks for this subject are locked",
    pickExamEmpty: "Pick an exam & subject to enter marks",
    absentCheckbox: "Absent",
    saveMarksFor: "Save marks for {count} students",
    savedEntriesToast: "Saved {count} entries",
  },
  staffCirculars: {
    title: "Circulars",
    close: "Close",
    subtitle: "School-wide announcements & notices",
    newCircular: "New circular",
     newEmergency: "New Emergency",
    composeCircular: "Compose circular",
    titleLabel: "Title",
    bodyLabel: "Body (HTML supported)",
    audience: "Audience",
    guardians: "Guardians",
    teachers: "Teachers",
    alsoEmail: "Also send by email",
    publishing: "Publishing...",
    publishNotify: "Publish & notify",
    publishedOn: "Published {date}",
    sentCount: "{count} sent",
    readCount: "{count} read",
    emptyState: "No circulars yet. Create your first one.",
      emptyEmergencyState: "No emergencies yet. Create your first one.",
    publishedToast: "Circular published",
  },
  nursery: {
    pageTitle: "Nursery / EYFS",
    pageSubtitle: "Early-years observations, photos & videos, document expiry",
    tabEyfs: "EYFS observations",
    tabGallery: "Photos & videos",
    tabDocs: "Document expiry",
    recentObservations: "Recent observations ({count})",
    newObservation: "New observation",
    logObservation: "Log EYFS observation",
    selectChild: "Select child",
    area: "Area",
    assessment: "Assessment",
    milestonePlaceholder: "Milestone (e.g. Recognises numerals 1-10)",
    observationPlaceholder: "What did you observe? (visible today, in the child's own actions)",
    save: "Save",
    observationLoggedToast: "Observation logged",
    downloadTermlyReport: "Download termly progress report",
    reportDownloadedToast: "Termly progress report downloaded",
    reportDownloadError: "Unable to download the termly progress report",
    albumsCount: "Albums ({count})",
    newAlbum: "New album",
    createAlbum: "Create album",
    description: "Description",
    create: "Create",
    albumCreatedToast: "Album created",
    mediaCount: "{count} media",
    expired: "Expired",
    d30: "≤ 30 days",
    d60: "31 – 60 days",
    d90: "61 – 90 days",
    expiring90Title: "Documents expiring in the next 90 days",
    thDate: "Date",
    thChild: "Child",
    thArea: "Area",
    thMilestone: "Milestone",
    thAssessment: "Assessment",
    thNote: "Note",
    thBucket: "Bucket",
    thPerson: "Person",
    thType: "Type",
    thKind: "Kind",
    thNumber: "Number",
    thExpires: "Expires",
    levelEmerging: "emerging",
    levelDeveloping: "developing",
    levelSecure: "secure",
    levelExceeding: "exceeding",
    noObservationsYet: "No observations yet. Click \"New observation\" to log the first one.",
    noAlbumsYet: "No albums yet. Teachers can upload photos here (parent consent enforced).",
    noDocsExpiring: "No documents expiring soon. Weekly cron emails principals with any that appear here.",
  },
  feePlan: {
    chargeWhenBought: "Charged when bought",
    chargeWhenBoughtHint: "No due for every student; charged at the counter whenever a student buys it.",
    monthlyDueDay: "Monthly fees due on day",
    monthlyDueDayHint: "Fee lines charged monthly are due on this day of every term month (1–28). Vacation months are not billed.",
    monthlyFeesDueTitle: "Monthly fees: due day",
    perMonth: "per month",
    otherCharges: "Other charges",
    arabicName: "Arabic name",
    term: "Term",
    custom: "Custom",
    standard: "Standard",
    oneTime: "One-time",
    amount: "Amount",
    type: "Type",
    feeHead: "Fee head",
    student: "Student",
    allowCustom: "Allow custom amount",
    allowCustomHint: "A student-specific amount can be set for this fee.",
    optionalHint: "Optional fees are charged only to students who have them enabled.",
    customMin: "Minimum (% of standard)",
    customMax: "Maximum (% of standard)",
    noLimit: "No limit",
    termCalendar: "Term calendar",
    termCalendarHint: "Choose the months of each term and vacation. A month can belong to one period only. Entered once per academic year.",
    termFeesDueTitle: "Per-term fees: instalment due dates",
    termFeesDueHint: "For fee lines charged per term, paid in one or two instalments.",
    monthTakenBy: "Already in {period}",
    pickDate: "Pick a date",
    clearDate: "Clear date",
    calendarNotSet: "Not set",
    calendarSaved: "Term calendar saved",
    saveCalendar: "Save calendar",
    installmentDue: "Instalment {n} due",
    vacationLabel: "Vacation name",
    notBilled: "Not billed",
    addVacation: "Add vacation",
    remove: "Remove",
    feePlan: "Fee plan",
    noStructure: "No published fee structure for this student's grade and academic year.",
    optionalEnabled: "Optional · enabled",
    optionalOff: "Optional · not enabled",
    customise: "Customise",
    customiseTitle: "Customise {name}",
    appliesToStudent: "Applies to this student",
    effectiveFrom: "Starts from",
    standardAmount: "Standard {amount}",
    allowedRange: "Allowed {min} – {max}",
    coveredMonths: "Months covered (suggests a pro-rata amount)",
    reason: "Reason",
    selectReason: "Select a reason",
    note: "Note",
    blankKeepsStandard: "Leave an amount blank to keep the standard amount.",
    planSaved: "Fee plan updated",
    termBalance: "Term balance",
    asOf: "As of {date}",
    expected: "Expected",
    discount: "Discount",
    paid: "Paid",
    balance: "Balance",
    position: "Position",
    previousBalance: "Previous balance",
    dueNow: "Due now",
    totalDue: "Total due",
    upcoming: "Upcoming",
    overdueSince: "Overdue since {date}",
    dueOn: "Due {date}",
    settled: "Paid",
    calendarMissing: "Set the term calendar in Academic Setup to track term balances.",
    noDues: "No dues recorded for this year.",
    notEnabledForStudent: "{name} is optional and not enabled for this student. Enable it in the student's fee plan.",
    alreadyCharged: "Charged",
    chargedLocked: "Already charged. Cancel that invoice to change the amount.",
    overdueOnly: "Overdue only",
    exportXlsx: "Export XLSX",
    buildDues: "Build / refresh dues",
    duesPreview: "{students} students · {plans} new fee plans · {created} dues to add · {updated} to update · {voided} to remove",
    duesUnmatched: "{count} existing charge(s) match no expected due and need review:",
    applyDues: "Apply",
    duesApplied: "Dues updated",
  },
  customFee: {
    monthlyFees: "Monthly fees",
    pickMonths: "Pick the months being paid",
    custom: "Custom…",
    suggested: "Suggested {amount}",
    saveToPlan: "Save to this student's fee plan",
    itemsBought: "Items bought",
    pickSizeFirst: "Choose the uniform size to pick items.",
    noItems: "No stock items found for this size.",
    qty: "Quantity",
    report: "Custom fees",
    covers: "Covers",
    charged: "Charged",
    difference: "Difference",
    enteredBy: "Entered by",
    invoice: "Invoice",
    noCustomFees: "No custom fees charged for this year.",
  },
  finance: {
    feeHeads: "Fee heads",
    grade: "Grade",
    feeStructures: "Fee structures",
    invoicesSubtitle: "Invoices, payment and fee structures",
    discounts: "Discounts",
    invoices: "Invoices",
    refunds: "Refunds",
    reports: "Reports",
    reportsDescription: "One-click XLSX exports for management, finance and admissions",
    download: "Download",
    downloaded: "downloaded",
    downloadFailed: "Download failed",
    generalLedger: "General ledger",
    chartAccounts: "Chart of accounts",
    trialBalance: "Trial balance",
    journal: "Journal",
    collection: "Collection",
    collectionToday: "Collection ({date})",
    today: "Today",
    mtd: "MTD",
    byMethodToday: "By method (today)",
    byMethod: "By method",
    monthlyCollection: "Monthly collection",
    monthTotal: "Month total",
    outstanding: "Outstanding",
    overdue: "Overdue",
    acrossPastDueInvoices: "across {count} past-due invoice(s)",
    acrossInvoices: "across {count} invoice(s)",
    code: "Code",
    account: "Account",
    type: "Type",
    debit: "Debit",
    credit: "Credit",
    total: "Total",
    amount: "Amount",
    gst: "GST %",
    balance: "Balance",
    date: "Date",
    narration: "Narration",
    reference: "Reference",
    lines: "Lines",
    name: "Name",
    status: "Status",
    action: "Action",
    method: "Method",
    currency: "Currency",
    payment: "Payment",
    and: "and",
    paid: "Paid",
    issued: "Issued",
    partiallyPaid: "Partially paid",
    cancelled: "Cancelled",
    online: "Online",
    cash: "Cash",
    bankTransfer: "Bank transfer",
    pos: "POS",
    cheque: "Cheque",
    neft: "NEFT",
    balanced: "Balanced",
    notBalanced: "Not balanced",
    asset: "Asset",
    expense: "Expense",
    liability: "Liability",
    income: "Income",
    equity: "Equity",
    newJournalEntry: "New journal entry",
    postEntry: "Post entry",
    journalPosted: "Journal entry posted",
    debitPaise: "Debit (paise)",
    creditPaise: "Credit (paise)",
    debitShort: "Dr",
    creditShort: "Cr",
    addLine: "Add line",
    noJournalEntries: "No journal entries yet.",
    noRefunds: "No refunds recorded.",
    noData: "No data available.",
    downloadReport: "Download report",
    recordRefund: "Record refund",
    refundAndCancel: "Refund & cancel",
    refundAndCancelInvoice: "Refund and cancel invoice",
    refundAndCancelExplainer: "This refunds the full collected amount and cancels the invoice. The refund and cancellation history is kept.",
    refundAndCancelled: "Invoice refunded and cancelled",
    fullRefundRequired: "The full paid amount must be refunded to cancel this invoice.",
    invoiceLevelRefund: "Refund at invoice level",
    invoiceLevelRefundHint: "Use the invoice total when this older invoice has no payment allocation on its fee lines.",
    applyVat: "Apply VAT",
    vatApplied: "VAT applied",
    refundRecorded: "Refund recorded",
    loading: "Loading…",
    loadError: "Unable to load finance data.",
    paymentCount: "{count} payment(s)",
    totalDebit: "Total Dr",
    totalCredit: "Total Cr",
    notBalancedShort: "not balanced",
    accountCount: "Chart of accounts ({count})",
    invoiceNumber: "Invoice #",
    student: "Student",
    period: "Period",
    allStatuses: "All statuses",
    unpaid: "unpaid",
    pdf: "PDF",
    recordPayment: "Record payment",
    academicYear: "Academic year",
    periodLabel: "Period label",
    generate: "Generate",
    generateInvoices: "Generate invoices",
    generating: "Generating...",
      selectStudent: "Select student",
      searchStudent: "Search by name or admission number",
      noStudentsFound: "No students found",
      required: "Required",
      amount: "Amount",
      issueDate: "Issue date",
      dueDate: "Due date",
      invoiceTotal: "Invoice total",
    feeBreakdown: "Fee breakdown",
    noFeesSelected: "No fees selected",
    tuitionOnly: "tuition only",
      notes: "Notes",
    paymentReference: "Reference / Cheque no. / Bank ref",
    recording: "Recording...",
    saving: "Saving...",
    paymentRecorded: "Payment recorded",
    upi: "UPI",
    card: "Card",
    invoicesGenerated: "{count} invoice(s) generated",
    feeHead: "Fee head",
    addFeeHead: "Add fee head",
    publishedStructures: "Published structures",
    addStructure: "Add structure",
    active: "Active",
    inactive: "Inactive",
    structureActivated: "Structure activated",
    structureDeactivated: "Structure deactivated",
    recurring: "Recurring",
    optional: "Optional",
    gstRate: "GST rate (basis points)",
    gstHint: "500 bps = 5% GST",
    edit: "Edit",
    yes: "Yes",
    no: "No",
    noFeeHeads: "No fee heads configured.",
    noFeeStructures: "No fee structures.",
    feeHeadCreated: "Fee head created",
    feeHeadUpdated: "Fee head updated",
    editFeeHead: "Edit fee head",
    createFeeHead: "Create fee head",
    update: "Update",
    create: "Create",
    save: "Save",
    structureName: "Structure name",
    lineItems: "Line items",
    feeHeadPlaceholder: "Fee head",
    selectGrade: "Select grade",
    allGrades: "All Grades",
    allAges: "All ages",
    oneTime: "One-time",
    monthly: "Monthly",
    term: "Term",
    annual: "Annual",
    structureCreated: "Fee structure created",
    structureUpdated: "Fee structure updated",
    editStructure: "Edit structure",
    creating: "Creating...",
    updating: "Updating...",
    term1: "Term 1",
    term2: "Term 2",
    term3: "Term 3",
    installmentsPerTerm: "Installments per term",
    studentFeePayment: "Student Fee Payment",
    studentFeePaymentDone: "Fee payment recorded",
    admissionNumber: "Admission Number",
    fullTerm: "Full Term",
    installment: "Installment",
    feeSelection: "Fee Selection",
    noFeeStructureForStudent: "No published fee structure for this student in the selected academic year.",
    otherFees: "Other Fees",
    paymentMode: "Payment Mode",
    manualCash: "Manual-Cash",
    online: "Online",
    pos: "POS",
    utr: "UTR Reference",
    utrPlaceholder: "e.g. UTR123456789",
    posReference: "POS Reference",
    posReferencePlaceholder: "e.g. POS123456789",
    paymentStatus: "Payment Status",
    recordAndGenerate: "Record Payment & Generate Invoice",
    siblingDiscount: "Sibling discount rules",
    discountRules: "Discount rules",
    editRules: "Edit rules",
    secondChildDiscount: "2nd child discount",
    twinDiscount: "Twin discount",
    scholarshipLevel1: "Scholarship level 1",
    scholarshipLevel2: "Scholarship level 2",
    twinEligibility: "Twin eligibility",
    markTwinEligible: "Mark child as twin-eligible",
    selectStudent: "Select child",
    discountType: "Discount type",
    selectDiscountType: "Select discount type",
    applyDiscount: "Apply discount",
    discount: "Discount",
    discountApplied: "Discount applied",
    discountEligibilityUpdated: "Discount eligibility updated",
    customDiscounts: "Custom discounts",
    createCustomDiscount: "Create custom discount",
    customDiscountName: "Discount name",
    percentage: "Percentage",
    fixedAmount: "Fixed amount",
    customDiscountCreated: "Custom discount created",
    customDiscountArchived: "Custom discount archived",
    studentPaymentDiscounts: "Discounts (optional)",
    noDiscountsAvailable: "No active discounts are available.",
    selectFeeBeforeDiscount: "Select at least one fee line to apply a discount.",
    discountsLoading: "Loading available discounts…",
    discountsLoadError: "Discounts could not be loaded. Try again.",
    archive: "Archive",
    archived: "Archived",
    discountTypes: { second_child: "2nd child discount", twin: "Twin discount", scholarship_level_1: "Scholarship level 1", scholarship_level_2: "Scholarship level 2" },
    enabled: "Enabled",
    disabled: "Disabled",
    noRulesConfigured: "No rules configured. Defaults: 10% for 2nd child, 50% for twins, 33.33% and 47.22% scholarship levels.",
    rulesUpdated: "Rules updated",
    saveRules: "Save rules",
    invoice: "Invoice",
    selectInvoice: "Select invoice",
    paidSuffix: "paid",
    maxRefundable: "Max refundable",
    cardReversal: "Card reversal",
    adjustment: "Adjustment",
    reason: "Reason",
    refundReasonPlaceholder: "e.g. Duplicate payment",
    periodPlaceholder: "e.g. Term 2",
    leaveGradesEmpty: "Leave grades empty to include all grades with published fee structures.",
    unpaidParenthetical: "unpaid",
    referencePlaceholder: "e.g. CHQ-000123 or NEFT-UTR",
    tuitionPlaceholder: "e.g. Tuition Fee",
    codePlaceholder: "e.g. TUIT",
    void: "Cancel",
    voidInvoice: "Cancel invoice",
    voided: "Invoice cancelled",
    voidExplainer: "Cancel an unpaid invoice, or one that has been fully refunded. The balance is zeroed but all history is kept. Invoices that still hold a payment must be refunded first.",
    voidReasonPlaceholder: "e.g. Duplicate bill",
    payByLineHint: "Enter an amount per fee line. The total is the sum of the lines.",
    refundByLineHint: "Enter a refund amount per fee line. The total is the sum of the lines.",
    scheduled: "Scheduled",
    voidedStatus: "Cancelled",
    refunded: "Refunded",
    waived: "Waived",
    issuedStatus: "Issued",
    duplicateFee: "This fee was already charged for this period",
    allocationRequired: "Provide an amount for each fee line",
    invoiceNumberShort: "Invoice",
    notRefundable: "This fee head is not refundable",
  },
  management: {
    studentsTitle: "Students",
    studentsSubtitle: "Directory of all enrolled students",
    studentsSearch: "Search by name or admission no…",
    studentsNoResults: "No students found",
    staffTitle: "Staff",
    staffSubtitle: "Teachers, accountants, principals, administrators & tenant owners",
    inviteStaff: "Invite staff",
    servicesTitle: "Student services",
    servicesSubtitle: "Transport, health, meals, events, hostel",
    announcementsTitle: "Announcements",
    announcementsSubtitle: "Manage events, circulars, and emergency announcements",
    libraryTitle: "Library",
    librarySubtitle: "Books catalogue and issue tracking",
    analyticsTitle: "Insights",
    analyticsSubtitle: "Live KPIs + predictive dropout risk (attendance × fees × discipline)",
    admissionsTitle: "Admissions",
    admissionsSubtitle: "Review applications and manage enrolment",
    attendanceTitle: "Attendance",
    attendanceSubtitle: "Monitor daily attendance across sections",
    timetableTitle: "Timetable",
    examsTitle: "Exams",
    campusTitle: "Campus operations",
    campusSubtitle: "Safety, discipline, alumni, placement, hostel services",
    institutionTitle: "Institution",
    institutionSubtitle: "Basic profile & contact information",
    dcumentsTitle: "Documents",
    dcumentsSubtitle: "Search uploaded documents across staff, admissions, and institution",
    searchDocumentsOption: "Search Documents",
    documentsExpiryOption: "Document expiry",
    documentsSearchLabel: "Search",
    documentsSearchPlaceholder: "Search by name, kind, number, application no…",
    documentsSearchSource: "Source",
    documentsSearchButton: "Search documents",
    documentsSearchNoResults: "No documents found",
    inspectionTab: "Inspection documents",
    inspectionTitle: "Inspection documents",
    inspectionSubtitle: "Keep the nursery's licensing, tenancy, safety, and regulatory records ready for inspection.",
    inspectionAdd: "Add document",
    inspectionEdit: "Edit document",
    inspectionDialogDescription: "Record the document details and attach its latest file. Rent cheques use the expiry field as their due date.",
    inspectionType: "Document type",
    inspectionSelectType: "Select document type",
    inspectionFile: "Document file",
    inspectionReplacementFile: "Replacement file (optional)",
    inspectionNumber: "Document number",
    inspectionChequeNumber: "Cheque number",
    inspectionIssueDate: "Issue date",
    inspectionExpiryDate: "Expiry date",
    inspectionChequeDueDate: "Cheque due date",
    inspectionExpiryOrDue: "Expiry / due date",
    inspectionNotes: "Notes",
    inspectionStatus: "Status",
    inspectionFiles: "Files",
    inspectionActions: "Actions",
    inspectionCurrent: "Current",
    inspectionNoDate: "No date",
    inspectionReminderNote: "Expiry and cheque due-date alerts appear inside EduOS at 30, 60, and 90 days. No external messages are sent.",
    inspectionNoInstitution: "No institution is linked to this account.",
    inspectionNoTypes: "No institution document types are configured in Platform Settings.",
    inspectionEmptyTitle: "No inspection documents yet",
    inspectionEmptyBody: "Add the nursery's first inspection or tenancy record.",
    inspectionLoadError: "Inspection documents could not be loaded.",
    inspectionCreated: "Inspection document added",
    inspectionUpdated: "Inspection document updated",
    inspectionDeleted: "Inspection document deleted",
    inspectionExpiryRequired: "An expiry date is required for this document type.",
    inspectionChequeDateRequired: "A cheque due date is required.",
    documentsSourceAll: "All",
    documentsSourceStaff: "Staff",
    documentsSourceAdmission: "Admission",
    documentsSourceInstitution: "Institution",
    documentsSource: "Source",
    documentsName: "Name",
    documentsType: "Type",
    documentsNumber: "Number",
    documentsExpiry: "Expiry",
    documentsReference: "Reference",
    documentsFiles: "Files",
    documentsRoleFather: "Father",
    documentsRoleMother: "Mother",
    documentsRoleChild: "Child",
  },
  academic: {
    title: "Academic setup",
    subtitle: "Manage levels, sections and subjects",
    gradesTab: "Grades",
    agesTab: "Age Groups",
    sectionsTab: "Sections",
    subjectsTab: "Subjects",
    yearsTab: "Academic Years",
    levelSystem: "Level system",
    gradeModeDesc: "Levels are named as grades (Class 1, Class 5 …).",
    ageModeDesc: "Levels are named by child age (45 days – 1 year, 1 – 2 years …).",
    switchToAges: "Switch to age groups",
    switchToGrades: "Switch to grades",
    switchToAgesTitle: "Switch this branch to age groups",
    switchToAgesDesc: "Pick the age band for every level. Levels with the same band merge into one age group — the class names are hidden everywhere and return when you switch back to grades.",
    switchToGradesTitle: "Switch this branch back to grades",
    switchToGradesDesc: "Each age group splits back into its original classes — sections, fees, and names return exactly as before.",
    switchToGradesHint: "Every merged class gets its original name, sections, and fee structures back. Confirm to continue.",
    editMapping: "Edit age group mapping",
    editMappingTitle: "Edit age group mapping",
    editMappingDesc: "Change which age band each level belongs to. Every level must have a band.",
    unmappedHint: "{count} levels still need an age band. Pick a band for every level to continue.",
    noLevelsYet: "No levels yet. Add age groups first or switch back to grades.",
    keepAsIs: "Keep as is",
    duplicateBandWarning: "Two or more levels are mapped to the same age band. That is allowed — every level will show the band label.",
    sharedBandNote: "{count} levels share this age band. Allowed — each keeps its own sections and fees.",
    confirmSwitch: "Confirm switch",
    switching: "Switching…",
    switchDone: "Level system updated",
    addGrade: "Add grade",
    addBand: "Add age group",
    chooseBand: "Choose age band",
    gradeAdded: "Grade added",
    bandAdded: "Age group added",
    displayOrder: "Display order",
    namePlaceholder: "Name (e.g. Class 9)",
    deleteLevel: "Delete level",
    levelDeleted: "Level deleted",
    outsideAllBands: "This child's age is outside the configured age bands. You can still continue.",
  },
};

export default en;
